import { useState, useEffect, useRef, useCallback } from "react";

interface ScrambleButtonProps {
  text: string;
  href?: string;
  className?: string;
  accentColor?: "emerald" | "cyan" | "purple";
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
}

export function ScrambleButton({
  text,
  href,
  className = "",
  accentColor = "emerald",
  onClick,
  type = "button",
  disabled = false,
}: ScrambleButtonProps) {
  const [displayText, setDisplayText] = useState(text);
  const [isFocusedOrHovered, setIsFocusedOrHovered] = useState(false);
  const intervalRef = useRef<number | null>(null);

  const chars =
    "!@#$%^&*()_+{}|:<>?[]-=/\\;,'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

  const triggerScramble = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
    }

    let iterations = 0;
    const maxIterations = text.length;

    intervalRef.current = window.setInterval(() => {
      setDisplayText(() => {
        return text
          .split("")
          .map((char, index) => {
            if (index < iterations) {
              return text[index];
            }
            if (char === " ") {
              return " ";
            }
            return chars[Math.floor(Math.random() * chars.length)];
          })
          .join("");
      });

      iterations += 0.5; // Resolve 1 character every 2 ticks (faster)

      if (iterations >= maxIterations) {
        setDisplayText(text);
        if (intervalRef.current !== null) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    }, 20);
  }, [text]);

  const resetText = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setDisplayText(text);
  }, [text]);

  useEffect(() => {
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    if (disabled) return;
    setIsFocusedOrHovered(true);
    triggerScramble();
  };

  const handleMouseLeave = () => {
    if (disabled) return;
    setIsFocusedOrHovered(false);
    resetText();
  };

  const handleFocus = () => {
    if (disabled) return;
    setIsFocusedOrHovered(true);
    triggerScramble();
  };

  const handleBlur = () => {
    if (disabled) return;
    setIsFocusedOrHovered(false);
    resetText();
  };

  const outerAccentStyles = {
    emerald: "bg-emerald-500/20 group-hover:bg-emerald-400 group-focus:bg-emerald-400",
    cyan: "bg-cyan-500/20 group-hover:bg-cyan-400 group-focus:bg-cyan-400",
    purple: "bg-purple-500/20 group-hover:bg-purple-400 group-focus:bg-purple-400",
  };

  const innerAccentStyles = {
    emerald:
      "bg-gray-950 group-hover:bg-emerald-950/30 text-emerald-400 group-hover:text-emerald-300 shadow-[inset_0_0_12px_rgba(16,185,129,0.15)] group-hover:shadow-[inset_0_0_12px_rgba(16,185,129,0.3)]",
    cyan: "bg-gray-950 group-hover:bg-cyan-950/30 text-cyan-400 group-hover:text-cyan-300 shadow-[inset_0_0_12px_rgba(6,182,212,0.15)] group-hover:shadow-[inset_0_0_12px_rgba(6,182,212,0.3)]",
    purple:
      "bg-gray-950 group-hover:bg-purple-950/30 text-purple-400 group-hover:text-purple-300 shadow-[inset_0_0_12px_rgba(168,85,247,0.15)] group-hover:shadow-[inset_0_0_12px_rgba(168,85,247,0.3)]",
  };

  const shadowStyles = {
    emerald:
      "hover:drop-shadow-[0_0_12px_rgba(16,185,129,0.45)] focus-within:drop-shadow-[0_0_12px_rgba(16,185,129,0.45)]",
    cyan: "hover:drop-shadow-[0_0_12px_rgba(6,182,212,0.45)] focus-within:drop-shadow-[0_0_12px_rgba(6,182,212,0.45)]",
    purple:
      "hover:drop-shadow-[0_0_12px_rgba(168,85,247,0.45)] focus-within:drop-shadow-[0_0_12px_rgba(168,85,247,0.45)]",
  };

  const clipPathStyle = {
    clipPath:
      "polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))",
    WebkitClipPath:
      "polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))",
  };

  const wrapperClassName = `relative inline-block transition-all duration-300 ${shadowStyles[accentColor]} ${className}`;

  const baseOuterClassName = `group w-full relative overflow-hidden flex items-center justify-center p-[1px] transition-all duration-300 backdrop-blur-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-gray-950 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${outerAccentStyles[accentColor]}`;

  const baseInnerClassName = `w-full h-full flex items-center justify-center px-8 py-4 font-mono text-sm uppercase transition-all duration-300 ${innerAccentStyles[accentColor]}`;

  const textGlowStyle =
    isFocusedOrHovered && !disabled
      ? {
          textShadow:
            accentColor === "emerald"
              ? "0 0 8px rgba(52, 211, 153, 0.85), 0 0 15px rgba(52, 211, 153, 0.45)"
              : accentColor === "cyan"
                ? "0 0 8px rgba(34, 211, 238, 0.85), 0 0 15px rgba(34, 211, 238, 0.45)"
                : "0 0 8px rgba(192, 132, 252, 0.85), 0 0 15px rgba(192, 132, 252, 0.45)",
        }
      : undefined;

  const buttonContent = (
    <div className={baseInnerClassName} style={clipPathStyle}>
      <style>{`
        @keyframes bgScan {
          0% { left: -160px; }
          50% { left: 100%; }
          100% { left: -160px; }
        }
      `}</style>
      {/* Diffused Background Scanner */}
      {!isFocusedOrHovered && !disabled && (
        <div
          className="absolute top-1/2 -translate-y-1/2 w-40 pointer-events-none opacity-25 mix-blend-screen rounded-full blur-2xl"
          style={{
            background:
              accentColor === "emerald"
                ? "radial-gradient(circle, rgba(52, 211, 153, 0.55) 0%, transparent 70%)"
                : accentColor === "cyan"
                  ? "radial-gradient(circle, rgba(34, 211, 238, 0.55) 0%, transparent 70%)"
                  : "radial-gradient(circle, rgba(192, 132, 252, 0.55) 0%, transparent 70%)",
            animation: "bgScan 4s ease-in-out infinite",
          }}
        />
      )}
      <span className="mr-2 opacity-50 group-hover:opacity-100 relative z-10" style={textGlowStyle}>
        [
      </span>
      <span className="relative z-10" style={textGlowStyle}>
        {displayText}
      </span>
      <span className="ml-2 opacity-50 group-hover:opacity-100 relative z-10" style={textGlowStyle}>
        ]
      </span>
    </div>
  );

  if (href) {
    return (
      <div className={wrapperClassName}>
        <a
          href={href}
          className={baseOuterClassName}
          style={clipPathStyle}
          onClick={onClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onFocus={handleFocus}
          onBlur={handleBlur}
        >
          {buttonContent}
        </a>
      </div>
    );
  }

  return (
    <div className={wrapperClassName}>
      <button
        type={type}
        disabled={disabled}
        className={baseOuterClassName}
        style={clipPathStyle}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        {buttonContent}
      </button>
    </div>
  );
}
